/**
 * Curated label-to-(emoji, sound) suggestions.
 *
 * The composer's per-class panel offers one-tap suggestions while the
 * kid types a display label. Match a word — "dog", "drum", "wave" —
 * and we propose a matching emoji + a fitting clip from the publish
 * sound library. The kid stays in control: tapping a suggestion
 * fills the slot, ignoring it changes nothing.
 *
 * The list is intentionally small (kid vocabulary, animals, gestures,
 * sounds). It's a delight, not a dictionary — too many entries make
 * the dropdown noise.
 *
 * Matching is keyword-anywhere, case-insensitive, on the *currently
 * typed label*. The class's training-time name is a separate input
 * that doesn't trigger suggestions (it's already named).
 */

import { getSound } from './sounds';

export interface LabelSuggestion {
  /** What the kid sees as the chip label. */
  label: string;
  /** Emoji proposed alongside the chip. */
  emoji: string;
  /** SoundId from the curated library. Undefined = no sound. */
  soundId?: string;
}

interface Entry {
  /** Words / fragments that activate this suggestion. Lowercase. */
  triggers: ReadonlyArray<string>;
  emoji: string;
  soundId?: string;
}

const ENTRIES: ReadonlyArray<Entry> = [
  // Animals — pair with their canonical clip when one exists.
  { triggers: ['dog', 'doggo', 'puppy', 'pup'], emoji: '🐶', soundId: 'bark' },
  { triggers: ['cat', 'kitty', 'kitten', 'meow'], emoji: '🐱', soundId: 'meow' },
  { triggers: ['cow', 'moo'], emoji: '🐮' },
  { triggers: ['sheep', 'lamb'], emoji: '🐑' },
  { triggers: ['horse', 'pony'], emoji: '🐴' },
  { triggers: ['pig', 'piggy', 'oink'], emoji: '🐷' },
  { triggers: ['bird', 'tweet', 'chirp'], emoji: '🐦' },
  { triggers: ['duck', 'quack'], emoji: '🦆' },
  { triggers: ['frog', 'toad', 'ribbit'], emoji: '🐸' },
  { triggers: ['mouse', 'rat'], emoji: '🐭' },
  { triggers: ['fish', 'swim'], emoji: '🐟' },
  // Gestures + body
  { triggers: ['wave', 'hi', 'hello', 'hey'], emoji: '👋' },
  { triggers: ['thumb', 'thumbs up', 'good', 'yes', 'agree'], emoji: '👍' },
  { triggers: ['thumbs down', 'no', 'bad'], emoji: '👎' },
  { triggers: ['peace'], emoji: '✌️' },
  { triggers: ['point'], emoji: '👉' },
  { triggers: ['clap', 'applause'], emoji: '👏', soundId: 'cheer' },
  { triggers: ['fist', 'rock'], emoji: '✊' },
  { triggers: ['ok', 'okay'], emoji: '👌' },
  { triggers: ['heart', 'love'], emoji: '❤️' },
  { triggers: ['smile', 'happy', 'grin'], emoji: '😀' },
  { triggers: ['sad', 'cry'], emoji: '😢' },
  { triggers: ['angry', 'mad'], emoji: '😠' },
  { triggers: ['surprised', 'wow', 'shock'], emoji: '😮' },
  { triggers: ['sleep', 'sleepy', 'tired'], emoji: '😴' },
  // Sounds + music
  { triggers: ['drum', 'beat'], emoji: '🥁', soundId: 'thud' },
  { triggers: ['bell', 'ding', 'ring'], emoji: '🔔', soundId: 'ding' },
  { triggers: ['guitar'], emoji: '🎸' },
  { triggers: ['piano'], emoji: '🎹' },
  { triggers: ['microphone', 'mic'], emoji: '🎤' },
  { triggers: ['music', 'song'], emoji: '🎵' },
  { triggers: ['speaker', 'loud'], emoji: '🔊' },
  { triggers: ['quiet', 'silent', 'shh'], emoji: '🤫' },
  // Objects + things
  { triggers: ['ball'], emoji: '⚽' },
  { triggers: ['robot'], emoji: '🤖', soundId: 'beep' },
  { triggers: ['rocket', 'fly'], emoji: '🚀', soundId: 'whoosh' },
  { triggers: ['fire', 'hot', 'flame'], emoji: '🔥' },
  { triggers: ['water', 'rain', 'splash'], emoji: '💧' },
  { triggers: ['star', 'twinkle', 'shine'], emoji: '⭐', soundId: 'twinkle' },
  { triggers: ['rainbow', 'colour', 'color'], emoji: '🌈' },
  { triggers: ['sun', 'sunny', 'day'], emoji: '☀️' },
  { triggers: ['moon', 'night'], emoji: '🌙' },
  { triggers: ['cloud'], emoji: '☁️' },
  { triggers: ['gift', 'present'], emoji: '🎁', soundId: 'fanfare' },
  { triggers: ['party', 'cheer', 'celebrate', 'win'], emoji: '🎉', soundId: 'cheer' },
  { triggers: ['confetti'], emoji: '🎊', soundId: 'cheer' },
  { triggers: ['pop', 'bubble'], emoji: '🫧', soundId: 'pop' },
  // Sorting / recycling labels
  { triggers: ['battery', 'batteries'], emoji: '🔋' },
  { triggers: ['paper'], emoji: '📄' },
  { triggers: ['cardboard', 'box'], emoji: '📦' },
  { triggers: ['biological', 'compost', 'food waste', 'organic'], emoji: '🍂' },
  { triggers: ['clothes', 'shirt'], emoji: '👕' },
  { triggers: ['glass', 'bottle'], emoji: '🫙' },
  { triggers: ['metal', 'can'], emoji: '🥫' },
  { triggers: ['plastic'], emoji: '♻️' },
  { triggers: ['shoes', 'shoe', 'sneaker'], emoji: '👟' },
  { triggers: ['trash', 'garbage', 'waste', 'rubbish'], emoji: '🗑️' },
  // Food (kids love this)
  { triggers: ['pizza'], emoji: '🍕' },
  { triggers: ['burger', 'hamburger'], emoji: '🍔' },
  { triggers: ['ice cream', 'icecream'], emoji: '🍦' },
  { triggers: ['donut', 'doughnut'], emoji: '🍩' },
  { triggers: ['apple'], emoji: '🍎' },
  { triggers: ['banana'], emoji: '🍌' },
  { triggers: ['cake'], emoji: '🎂' },
];

/** Token-aware matcher.
 *  - Multi-word triggers ("thumbs up") match as a substring of text.
 *  - Single-word triggers match a word in the typed text exactly,
 *    OR as a prefix when the kid is mid-typing ("do" → "dog").
 *  Bidirectional `includes` is too aggressive — "no" inside
 *  "unknown" would otherwise surface 👎 for a label that means
 *  nothing of the kind. */
function _matchesTrigger(text: string, trigger: string): boolean {
  if (trigger.includes(' ')) return text.includes(trigger);
  for (const token of text.split(/\s+/)) {
    if (!token) continue;
    if (token === trigger) return true;
    // Kid is mid-typing — accept the typed token as a prefix of
    // the trigger ("do" → "dog"). Need ≥2 chars to avoid one-letter
    // false positives.
    if (token.length >= 2 && trigger.startsWith(token)) return true;
  }
  return false;
}

/** Return up to `limit` suggestions for the typed label. Empty array
 *  when nothing matches — the caller should skip rendering rather
 *  than show a "no results" state. */
export function suggestForLabel(raw: string, limit = 4): ReadonlyArray<LabelSuggestion> {
  const text = raw.trim().toLowerCase();
  if (!text || text.length < 2) return [];
  const out: LabelSuggestion[] = [];
  const seen = new Set<string>();
  for (const entry of ENTRIES) {
    for (const trigger of entry.triggers) {
      if (_matchesTrigger(text, trigger)) {
        const key = entry.emoji + (entry.soundId ?? '');
        if (seen.has(key)) break;
        seen.add(key);
        out.push({
          label: trigger,
          emoji: entry.emoji,
          ...(entry.soundId ? { soundId: entry.soundId } : {}),
        });
        break;
      }
    }
    if (out.length >= limit) break;
  }
  return out;
}

/** Resolve a sound id back to its catalogue entry — convenience for
 *  the suggestion preview that shows the sound name alongside the
 *  emoji. */
export function describeSuggestionSound(s: LabelSuggestion): string | undefined {
  if (!s.soundId) return undefined;
  return getSound(s.soundId)?.label;
}
